import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useNotify } from "@/hooks/useNotify";

const ROLE_CHECK_INTERVAL_MS = 30 * 1000;

const RoleChangeWatcher = () => {
  const { data: session, status, update } = useSession();
  const router = useRouter();
  const notify = useNotify();
  const knownRoleRef = useRef<string | null>(null);

  useEffect(() => {
    if (status !== "authenticated") {
      knownRoleRef.current = null;
      return;
    }

    const currentRole = session?.user?.userRole ?? null;

    if (knownRoleRef.current === null) {
      knownRoleRef.current = currentRole;
      return;
    }

    if (currentRole && currentRole !== knownRoleRef.current) {
      knownRoleRef.current = currentRole;
      notify(
        "info",
        "Role updated",
        "Your account role has changed, so you've been redirected to the home page.",
      );
      router.replace("/home");
    }
  }, [status, session?.user?.userRole, notify, router]);

  useEffect(() => {
    if (status !== "authenticated") {
      return;
    }

    const interval = setInterval(() => {
      update();
    }, ROLE_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [status, update]);

  useEffect(() => {
    if (status !== "authenticated") {
      return;
    }

    const handleRouteChangeComplete = () => {
      update();
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    return () =>
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
  }, [status, update, router.events]);

  useEffect(() => {
    if (status !== "authenticated") {
      return;
    }

    const handleActivity = () => {
      if (document.visibilityState === "visible") {
        update();
      }
    };

    window.addEventListener("focus", handleActivity);
    document.addEventListener("visibilitychange", handleActivity);
    return () => {
      window.removeEventListener("focus", handleActivity);
      document.removeEventListener("visibilitychange", handleActivity);
    };
  }, [status, update]);

  return null;
};

export default RoleChangeWatcher;
